import type { SearchParameters } from 'ofetch'
import type { AvailableRouterMethod, NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import {
  handleError,
  handleRequest,
  handleRequestError,
  handleResponse,
  type ResOptions
} from '~/composables/httpHelper'

const fetch = async <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>, T = any>(
  url: string,
  opts: NitroFetchOptions<R, M>
) => {
  const meta: Record<string, any> = {}
  const { public: env } = useRuntimeConfig()
  //baseUrl 为空时 使用NUXT 代理
  const baseUrl = import.meta.client && !url.includes('ossApi') ? env.config.API_BASE_URL : ''

  const token = useCookie<string | undefined>('access_token')
  const { $i18n } = useNuxtApp()

  const locale = $i18n.locale.value as keyof typeof ELocale

  // 🔧 根据环境设置默认超时
  const isSSR = import.meta.server
  const defaultTimeout = isSSR ? 6000 : 10000 // SSR 6秒，客户端 10秒

  return $fetch<ResOptions<T>>(url, {
    baseURL: baseUrl,
    
    // 🔧 关键修改：添加超时配置
    timeout: opts.timeout || defaultTimeout,
    
    // 🔧 优化重试策略：SSR 只重试 1 次
    retry: isSSR ? 1 : 2,
    retryDelay: 300, // 减少重试延迟
    
    // 🔧 重试条件：只在特定状态码时重试，避免超时重试
    retryStatusCodes: [408, 425, 429, 500, 502, 503, 504],
    
    // 请求拦截
    onRequest(ctx) {
      handleRequest(meta, token, locale, ctx, opts)
    },
    // 请求错误拦截
    onRequestError(ctx) {
      handleRequestError(ctx, token)
    },
    // 响应拦截
    onResponse(ctx) {
      handleResponse(meta, token, ctx)
    },
    // 错误拦截
    onResponseError(ctx) {
      handleError(ctx, token)
    },
    // 合并 options
    ...opts
  })
}

export const useHttp = {
  get: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    params?: SearchParameters,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'GET', params, ...option }) // .then(res => res.data.value);
  },

  post: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    //   console.log('40-40', {...option})
    return fetch(url, { method: 'POST', body, ...option }) // .then(res => res.data.value);
  },

  put: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'PUT', body, ...option }) // .then(res => res.data.value);
  },

  delete: <R extends NitroFetchRequest, M extends AvailableRouterMethod<R>>(
    url: string,
    body?: RequestInit['body'] | Record<string, any>,
    option?: NitroFetchOptions<R, M>
  ) => {
    return fetch(url, { method: 'DELETE', body, ...option }) // .then(res => res.data.value);
  }
}
